import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from "@shared/store/dialog.duck";
import DialogHeader from "@shared/dialogs/DialogHeader";
import Dialog from '@shared/ui/molecules/Dialog';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { isCompanyAdminUser, isOfficeAdminUser } from "@shared/utils/AccessControl";
import { selectCurrentUser } from "@shared/store/auth.duck";
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import { LoadingOverlay } from '@shared/ui/molecules/LoadingOverlay';
import TextField from '@shared/ui/atoms/TextField';
import { Switch } from '@shared/ui/atoms/Switch';
import { Desk, getDesk, updateDesk } from '@features/desks/services/DeskBookingService';
import { failureNotification, successNotification } from "@shared/store/notification.duck";
import { fetchDesks } from '@features/desks/store/deskBooking.duck';

interface Props {
  payload?: {deskId: number};
}

export default function EditDeskDialog(props: Props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const hasAccess = useMemo(() => isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser), [currentUser]);
  const [loading, setLoading] = useState(false);
  const [hasRequested, setHasReqested] = useState(false);
  const [label, setLabel] = useState('');
  const [desk, setDesk] = useState<Desk | undefined>(undefined);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (props.payload && !hasRequested && !loading) {
      setLoading(true);
      setHasReqested(true);
      getDesk(props.payload?.deskId)
        .then((desk: Desk) => {
          setLabel(desk.label);
          setIsActive(desk.active);
          setDesk(desk);
        })
        .catch(() => {
          dispatch(failureNotification('Failed to load desk'));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, loading, props.payload, hasRequested]);

  const onUpdateClick = async () => {
    if (desk) {
      setLoading(true);
      await updateDesk({
        ...desk,
        label: label,
        active: isActive,
      });
      dispatch(fetchDesks(desk.officeId));
      dispatch(successNotification('Desk updated'));
      close();
    }
  }

  const close = () => {
    setLoading(false);
    dispatch(closeDialog());
  }

  return (
    <Dialog isOpen={true} onClose={close} showLogo={true}>
      <DialogHeader title="dialog-headers.update-desk" />
      <AccessRequired teamAdminsRequired={false} officeAdminsRequired={true} companyAdminsRequired={true} visible={!hasAccess} />

      {loading && <LoadingOverlay />}

      {hasAccess && <div>
        <TextField label={"Desk label"} onChange={setLabel} fullWidth={true} value={label} />
        <Switch style={{marginBottom: 32}} value={isActive} label={"Is active"} onChange={setIsActive} />
        <OutlineButton click={onUpdateClick} text="button.save-changes" fullWidth={true} disabled={!hasAccess} />
      </div>}

    </Dialog>
  )
}
